import { ColumnDef, SortingFnOption, Table } from '@tanstack/react-table';
import { BaseCell } from 'src/components/TableV2/cells';
import { IconTagCell } from 'src/components/TableV2/cells/IconTagCell';
import { Content } from 'src/contexts/ContentContext';
import { TeamMembersIcon } from 'src/svgs/TeamMembersIcon';
import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from 'src/tables/SalesTable/SalesTable.type';
import { TableShimmeringDiv } from 'src/tables/Table';

import { multiSelectFilter } from '../filters/filterFunctions';
import { useCombinedUsers } from './userTagColumnHooks';

const COLUMN_MIN_SIZE = 50;
const COLUMN_MAX_SIZE = 500;

interface UserTagColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to get user IDs from data */
  accessor: (data: TData) => string[] | string | null | undefined;
  /**
   * Custom hook to resolve user IDs to display names
   * Returns { displayName: string, initials: string }
   */
  useUserDisplayHook?: (userIds: string[] | string | null | undefined) => {
    displayName: string | null;
    initials: string;
    isTeam: boolean;
  };
  /** Column size */
  size?: number;
  /** Min size */
  minSize?: number;
  /** Max size */
  maxSize?: number;
  /** Enable column filter */
  enableColumnFilter?: boolean;
  /** Show as icon tag (default: true). If false, shows plain text */
  showIconTag?: boolean;
  sortingFn?: SortingFnOption<TData>;
  shouldRender?: (data: TData, table: Table<TData>) => boolean;
  filterFn?: (row: any, columnId: string, filterValue: any) => boolean;
  meta?: Record<string, any>;
}

/**
 * Factory function to create user tag column definitions.
 * Less opinionated - delegates user resolution to a custom hook.
 *
 * @example
 * // With context-based users
 * createUserTagColumn({
 *   id: SalesTableColumnId.PurchasedBy,
 *   headerContentId: CONTENT_ID,
 *   accessor: (data) => data?.sale?.owners?.buyerIds,
 *   useUserDisplayHook: useContextBasedUsers,
 * })
 *
 * @example
 * // With API-fetched users
 * createUserTagColumn({
 *   id: SalesTableColumnId.OriginatedBy,
 *   headerContentId: CONTENT_ID,
 *   accessor: (data) => data?.sale?.origBy,
 *   useUserDisplayHook: useApiFetchedUser,
 *   showIconTag: false,
 * })
 */
export const createUserTagColumn = <TData,>(
  options: UserTagColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    useUserDisplayHook = useCombinedUsers,
    size,
    minSize = COLUMN_MIN_SIZE,
    maxSize = COLUMN_MAX_SIZE,
    enableColumnFilter = true,
    showIconTag = true,
    sortingFn,
    shouldRender,
    filterFn,
  } = options;

  return {
    id,
    enableColumnFilter,
    size,
    minSize,
    maxSize,
    header: ({ column: { id } }) => <Content id={headerContentId} />,
    accessorFn: accessor,
    cell: function Cell({ getValue, row: { original }, table }) {
      const userIds = getValue<string[] | string | null>();
      const { displayName, initials, isTeam } = useUserDisplayHook(userIds);

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (shouldRender && !shouldRender(original, table)) {
        return null;
      }

      if (!displayName) {
        return <BaseCell />;
      }

      if (showIconTag) {
        return (
          <IconTagCell
            iconLeftContent={<>{initials}</>}
            text={displayName}
            iconRight={isTeam ? <TeamMembersIcon /> : undefined}
          />
        );
      }

      return (
        <BaseCell>
          {displayName}
          {isTeam && <TeamMembersIcon />}
        </BaseCell>
      );
    },
    ...(sortingFn && { sortingFn }),
    meta: {
      columnFilterVariant: 'userTag',
      ...options.meta,
    },
    filterFn: filterFn ?? multiSelectFilter,
  };
};
